/** Max edge (px) for exported square avatar JPEG. */
export const AVATAR_EXPORT_MAX_EDGE = 512;
export const AVATAR_EXPORT_QUALITY = 0.9;

export type CropAreaPixels = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export function clampAvatarExportEdge(cropEdge: number, maxEdge = AVATAR_EXPORT_MAX_EDGE): number {
  const n = Math.round(cropEdge);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(maxEdge, n);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', () => reject(new Error('Image load failed')));
    // blob:/data: — never set crossOrigin (breaks load / taints canvas).
    if (/^https?:\/\//i.test(src)) image.crossOrigin = 'anonymous';
    image.src = src;
  });
}

function rotatedBoundingBox(width: number, height: number, rotationDeg: number) {
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  return {
    width: Math.round(width * cos + height * sin),
    height: Math.round(width * sin + height * cos),
  };
}

/**
 * Crop + rotate source image; export square JPEG (edge ≤ {@link AVATAR_EXPORT_MAX_EDGE}).
 * `pixelCrop` is in the rotated-image coordinate space (react-easy-crop `croppedAreaPixels`).
 */
export async function getCroppedImage(
  imageSrc: string,
  pixelCrop: CropAreaPixels,
  rotation = 0,
  maxEdge = AVATAR_EXPORT_MAX_EDGE,
): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const rotRad = (rotation * Math.PI) / 180;
  const bBox = rotatedBoundingBox(image.width, image.height, rotation);

  const rotCanvas = document.createElement('canvas');
  rotCanvas.width = Math.max(1, bBox.width);
  rotCanvas.height = Math.max(1, bBox.height);
  const rotCtx = rotCanvas.getContext('2d');
  if (!rotCtx) throw new Error('Canvas 2d unavailable');

  rotCtx.translate(bBox.width / 2, bBox.height / 2);
  rotCtx.rotate(rotRad);
  rotCtx.drawImage(image, -image.width / 2, -image.height / 2);

  const cropW = Math.max(1, Math.round(pixelCrop.width));
  const cropH = Math.max(1, Math.round(pixelCrop.height));
  const out = clampAvatarExportEdge(Math.min(cropW, cropH), maxEdge);

  const canvas = document.createElement('canvas');
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2d unavailable');

  ctx.drawImage(
    rotCanvas,
    Math.round(pixelCrop.x),
    Math.round(pixelCrop.y),
    cropW,
    cropH,
    0,
    0,
    out,
    out,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('JPEG export failed'))),
      'image/jpeg',
      AVATAR_EXPORT_QUALITY,
    );
  });
}

export function avatarFileFromBlob(blob: Blob, name = 'profile.jpg'): File {
  return new File([blob], name, { type: blob.type || 'image/jpeg' });
}
